import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image, TouchableOpacity, KeyboardAvoidingView, Platform} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Snackbar,
  Checkbox,
  Menu,
  Divider,
  IconButton,
} from 'react-native-paper';
import GradientHeader from '../../components/common/GradientHeader';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useForm, Controller} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {launchImageLibrary} from 'react-native-image-picker';
import DateTimePicker from '@react-native-community/datetimepicker';
import {useCustomerStore} from '../../store/useCustomerStore';
import {useAuthStore} from '../../store/useAuthStore';
import PhoneInput from '../../components/common/PhoneInput';
import {colors, spacing} from '../../theme';

const SectionHeader = ({title}) => (
  <Text style={styles.sectionHeader}>{title}</Text>
);

const CustomerFormScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const customerId = route.params?.customerId;
  const isEditing = !!customerId;
  const returnTo = route.params?.returnTo;
  const prefillPhone = route.params?.prefillPhone;
  const prefillName = route.params?.prefillName;

  const {
    currentCustomer,
    fetchCustomerDetail,
    createCustomer,
    updateCustomer,
    uploadCustomerImages,
    referralSources,
    fetchReferralSources,
  } = useCustomerStore();

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [showAltPhone, setShowAltPhone] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [referralMenuVisible, setReferralMenuVisible] = useState(false);
  const tenantCountryCode = useAuthStore(s => s.user?.tenant?.countryCode) || '+91';
  const [countryCode, setCountryCode] = useState(tenantCountryCode);
  const [faceImage, setFaceImage] = useState(null);
  const [bodyImage, setBodyImage] = useState(null);
  const [facePreview, setFacePreview] = useState(null);
  const [bodyPreview, setBodyPreview] = useState(null);

  const schema = yup.object().shape({
    fullName: yup.string().required(t('validation.required')),
    phone: yup.string(),
    altPhone: yup.string(),
    email: yup.string().email(t('validation.email')),
    address: yup.string(),
    city: yup.string(),
    pincode: yup.string(),
    notes: yup.string(),
    dateOfBirth: yup.string(),
    gender: yup.string().required(t('validation.required')),
    heightCm: yup.string(),
    weightKg: yup.string(),
    referralSourceId: yup.number().nullable(),
  });

  const {control, handleSubmit, reset, setValue, watch, formState: {errors}} = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      fullName: prefillName || '', phone: prefillPhone || '', altPhone: '', email: '',
      address: '', city: '', pincode: '', notes: '',
      dateOfBirth: '', gender: '', heightCm: '', weightKg: '',
      referralSourceId: null,
    },
  });

  const selectedReferralId = watch('referralSourceId');
  const selectedReferralName = referralSources.find(
    s => s.id === selectedReferralId,
  )?.name;

  useEffect(() => {
    fetchReferralSources();
  }, [fetchReferralSources]);

  useEffect(() => {
    if (isEditing) {
      fetchCustomerDetail(customerId);
    }
  }, [isEditing, customerId, fetchCustomerDetail]);

  useEffect(() => {
    if (isEditing && currentCustomer) {
      const hasAddress = currentCustomer.address || currentCustomer.city || currentCustomer.pincode;
      setShowAddress(!!hasAddress);
      if (currentCustomer.altPhone) setShowAltPhone(true);
      if (currentCustomer.countryCode) setCountryCode(currentCustomer.countryCode);
      if (currentCustomer.faceImageUrl) setFacePreview(currentCustomer.faceImageUrl);
      if (currentCustomer.bodyImageUrl) setBodyPreview(currentCustomer.bodyImageUrl);
      reset({
        fullName: currentCustomer.fullName || '',
        phone: currentCustomer.phone || '',
        altPhone: currentCustomer.altPhone || '',
        email: currentCustomer.email || '',
        address: currentCustomer.address || '',
        city: currentCustomer.city || '',
        pincode: currentCustomer.pincode || '',
        notes: currentCustomer.notes || '',
        dateOfBirth: currentCustomer.dateOfBirth || '',
        gender: currentCustomer.gender || '',
        heightCm: currentCustomer.heightCm ? String(currentCustomer.heightCm) : '',
        weightKg: currentCustomer.weightKg ? String(currentCustomer.weightKg) : '',
        referralSourceId: currentCustomer.referralSourceId || null,
      });
    }
  }, [isEditing, currentCustomer, reset]);

  const pickImage = useCallback(async (setter, previewSetter) => {
    const result = await launchImageLibrary({
      mediaType: 'photo',
      maxWidth: 1024,
      maxHeight: 1024,
      quality: 0.8,
    });
    if (!result.didCancel && result.assets?.[0]) {
      setter(result.assets[0]);
      previewSetter(result.assets[0].uri);
    }
  }, []);

  const onSubmit = useCallback(async data => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        countryCode,
        heightCm: data.heightCm ? parseFloat(data.heightCm) : null,
        weightKg: data.weightKg ? parseFloat(data.weightKg) : null,
        dateOfBirth: data.dateOfBirth || null,
        gender: data.gender || null,
        referralSourceId: data.referralSourceId || null,
      };

      if (!showAltPhone) {
        payload.altPhone = '';
      }

      if (!showAddress) {
        payload.address = '';
        payload.city = '';
        payload.pincode = '';
      }

      let customer;
      if (isEditing) {
        customer = await updateCustomer(customerId, payload);
      } else {
        customer = await createCustomer(payload);
      }

      if (faceImage || bodyImage) {
        await uploadCustomerImages(customer.id, faceImage, bodyImage);
      }

      if (returnTo && !isEditing) {
        navigation.navigate(returnTo, {newCustomer: customer});
      } else {
        navigation.goBack();
      }
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [isEditing, customerId, createCustomer, updateCustomer, uploadCustomerImages, navigation, t, showAltPhone, showAddress, faceImage, bodyImage]);

  const renderImagePicker = (label, preview, onPick) => (
    <TouchableOpacity style={styles.imagePicker} onPress={onPick}>
      {preview ? (
        <Image source={{uri: preview}} style={styles.imagePreview} />
      ) : (
        <View style={styles.imagePlaceholder}>
          <Text style={styles.imagePlaceholderIcon}>+</Text>
          <Text style={styles.imagePlaceholderText}>{label}</Text>
          <Text style={styles.tapToUpload}>{t('customer.tapToUpload')}</Text>
        </View>
      )}
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <GradientHeader
        title={isEditing ? t('customer.editCustomer') : t('customer.addCustomer')}
        onBack={() => navigation.goBack()}
      />

      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
        {/* Basic Info */}
        <SectionHeader title={t('customer.title')} />
        <Controller
          control={control}
          name="fullName"
          render={({field: {onChange, onBlur, value}}) => (
            <TextInput
              label={t('customer.name') + ' *'}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              error={!!errors.fullName}
              mode="outlined"
              style={styles.input}
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({field: {onChange, value}}) => (
            <PhoneInput
              countryCode={countryCode}
              onCountryCodeChange={setCountryCode}
              phone={value}
              onPhoneChange={onChange}
              label={t('customer.phone')}
            />
          )}
        />
        {showAltPhone ? (
          <Controller
            control={control}
            name="altPhone"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('customer.altPhone')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                mode="outlined"
                style={styles.input}
                keyboardType="phone-pad"
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            )}
          />
        ) : (
          <TouchableOpacity
            onPress={() => setShowAltPhone(true)}
            style={styles.addLinkRow}>
            <Text style={styles.addLinkText}>
              + {t('customer.addAltPhone')}
            </Text>
          </TouchableOpacity>
        )}
        <Controller
          control={control}
          name="email"
          render={({field: {onChange, onBlur, value}}) => (
            <TextInput
              label={t('customer.email')}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              error={!!errors.email}
              mode="outlined"
              style={styles.input}
              keyboardType="email-address"
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
            />
          )}
        />

        {/* Personal Details */}
        <SectionHeader title={t('customer.personalDetails')} />
        <Controller
          control={control}
          name="dateOfBirth"
          render={({field: {onChange, value}}) => {
            const parsedDate = value ? new Date(value) : null;
            const isValidDate = parsedDate && !isNaN(parsedDate.getTime());
            return (
              <>
                <TouchableOpacity activeOpacity={0.7} onPress={() => setShowDatePicker(true)}>
                  <View pointerEvents="none">
                    <TextInput
                      label={t('customer.dateOfBirth')}
                      value={value || ''}
                      mode="outlined"
                      style={styles.input}
                      editable={false}
                      placeholder="YYYY-MM-DD"
                      outlineColor={colors.border}
                      activeOutlineColor={colors.primary}
                      right={<TextInput.Icon icon="calendar" />}
                    />
                  </View>
                </TouchableOpacity>
                {showDatePicker && (
                  <DateTimePicker
                    value={isValidDate ? parsedDate : new Date(2000, 0, 1)}
                    mode="date"
                    display="default"
                    maximumDate={new Date()}
                    onChange={(event, selectedDate) => {
                      setShowDatePicker(false);
                      if (event.type === 'set' && selectedDate) {
                        const y = selectedDate.getFullYear();
                        const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
                        const d = String(selectedDate.getDate()).padStart(2, '0');
                        onChange(`${y}-${m}-${d}`);
                      }
                    }}
                  />
                )}
              </>
            );
          }}
        />

        <Text style={styles.fieldLabel}>{t('customer.gender')} *</Text>
        <Controller
          control={control}
          name="gender"
          render={({field: {onChange, value}}) => (
            <View style={styles.radioRow}>
              <TouchableOpacity
                style={[styles.genderOption, value === 'MALE' && styles.genderOptionSelected]}
                onPress={() => onChange('MALE')}
                activeOpacity={0.7}>
                <IconButton
                  icon={value === 'MALE' ? 'check-circle' : 'circle-outline'}
                  size={22}
                  iconColor={value === 'MALE' ? colors.primary : colors.textSecondary}
                  style={styles.genderIcon}
                />
                <Text style={[styles.genderLabel, value === 'MALE' && styles.genderLabelSelected]}>
                  {t('customer.male')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.genderOption, value === 'FEMALE' && styles.genderOptionSelected]}
                onPress={() => onChange('FEMALE')}
                activeOpacity={0.7}>
                <IconButton
                  icon={value === 'FEMALE' ? 'check-circle' : 'circle-outline'}
                  size={22}
                  iconColor={value === 'FEMALE' ? colors.primary : colors.textSecondary}
                  style={styles.genderIcon}
                />
                <Text style={[styles.genderLabel, value === 'FEMALE' && styles.genderLabelSelected]}>
                  {t('customer.female')}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        />
        {errors.gender && <Text style={styles.errorText}>{errors.gender.message}</Text>}

        <View style={styles.row}>
          <View style={styles.halfField}>
            <Controller
              control={control}
              name="heightCm"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('customer.height')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  keyboardType="numeric"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />
          </View>
          <View style={styles.halfField}>
            <Controller
              control={control}
              name="weightKg"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('customer.weight')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  keyboardType="numeric"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />
          </View>
        </View>

        {/* Referral Source */}
        <SectionHeader title={t('customer.referralSource')} />
        <Menu
          visible={referralMenuVisible}
          onDismiss={() => setReferralMenuVisible(false)}
          anchor={
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => setReferralMenuVisible(true)}>
              <View pointerEvents="none">
                <TextInput
                  label={t('customer.referralSource')}
                  value={selectedReferralName || ''}
                  mode="outlined"
                  style={styles.input}
                  editable={false}
                  right={<TextInput.Icon icon="chevron-down" />}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              </View>
            </TouchableOpacity>
          }
          anchorPosition="bottom"
          style={styles.menu}>
          {referralSources.map(source => (
            <Menu.Item
              key={source.id}
              onPress={() => {
                setValue('referralSourceId', source.id);
                setReferralMenuVisible(false);
              }}
              title={source.name}
            />
          ))}
        </Menu>

        {/* Customer Photos */}
        <SectionHeader title={t('customer.customerPhotos')} />
        <View style={styles.imageRow}>
          {renderImagePicker(
            t('customer.faceImage'),
            facePreview,
            () => pickImage(setFaceImage, setFacePreview),
          )}
          {renderImagePicker(
            t('customer.bodyImage'),
            bodyPreview,
            () => pickImage(setBodyImage, setBodyPreview),
          )}
        </View>

        {/* Address Toggle */}
        <View style={styles.checkboxRow}>
          <View style={styles.checkboxBorder}>
            <Checkbox
              status={showAddress ? 'checked' : 'unchecked'}
              onPress={() => setShowAddress(!showAddress)}
              color={colors.primary}
              uncheckedColor={colors.border}
            />
          </View>
          <Text style={styles.checkboxLabel}>{t('customer.showAddress')}</Text>
        </View>

        {showAddress && (
          <View style={styles.addressBlock}>
            <Controller
              control={control}
              name="address"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('customer.address')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  multiline
                  numberOfLines={3}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />
            <Controller
              control={control}
              name="city"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('customer.city')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />
            <Controller
              control={control}
              name="pincode"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('customer.pincode')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={[styles.input, {marginBottom: 0}]}
                  keyboardType="numeric"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />
          </View>
        )}

        {/* Notes */}
        <Divider style={styles.divider} />
        <Controller
          control={control}
          name="notes"
          render={({field: {onChange, onBlur, value}}) => (
            <TextInput
              label={t('customer.notes')}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              mode="outlined"
              style={styles.input}
              multiline
              numberOfLines={3}
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
            />
          )}
        />

        {Object.keys(errors).length > 0 && (
          <Text style={styles.errorText}>
            {errors[Object.keys(errors)[0]]?.message}
          </Text>
        )}

        <Button
          mode="contained"
          onPress={handleSubmit(onSubmit)}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}
          buttonColor={colors.primary}>
          {t('common.save')}
        </Button>
      </ScrollView>
      </KeyboardAvoidingView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}
        action={{label: t('common.ok'), onPress: () => setSnackbar('')}}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  form: {padding: spacing.md},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  errorText: {color: colors.error, marginBottom: spacing.sm, fontSize: 12},
  saveButton: {marginTop: spacing.md, marginBottom: spacing.xxl, borderRadius: 8},
  sectionHeader: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  fieldLabel: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
    marginLeft: spacing.xs,
  },
  radioRow: {
    flexDirection: 'row',
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  genderOption: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingRight: spacing.md,
    backgroundColor: colors.surface,
  },
  genderOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: '#E8F5E9',
  },
  genderIcon: {
    margin: 0,
  },
  genderLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  genderLabelSelected: {
    color: colors.primary,
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  halfField: {
    flex: 1,
  },
  menu: {
    marginTop: spacing.xs,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.xl,
  },
  checkboxBorder: {
    borderWidth: 1,
    borderColor: colors.textSecondary,
    borderRadius: 10,
    marginRight: spacing.sm,
  },
  checkboxLabel: {
    fontSize: 14,
    color: colors.text,
  },
  addressBlock: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  addLinkRow: {
    marginBottom: spacing.sm,
    paddingVertical: spacing.xs,
  },
  addLinkText: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '500',
  },
  divider: {
    marginVertical: spacing.md,
  },
  imageRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  imagePicker: {
    flex: 1,
    height: 150,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
    overflow: 'hidden',
    backgroundColor: colors.surface,
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
  imagePlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholderIcon: {
    fontSize: 32,
    color: colors.textLight,
  },
  imagePlaceholderText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  tapToUpload: {
    fontSize: 11,
    color: colors.textLight,
    marginTop: 2,
  },
});

export default CustomerFormScreen;
